import { useNavigate, Link } from 'react-router-dom';
import { ArrowLeft, FileText, Scale, CreditCard, ShieldCheck, AlertTriangle, Globe, Mail, Ban, Briefcase } from 'lucide-react';
import { Button } from '../components/ui/button';
import { usePageMeta } from '../lib/usePageMeta';

export default function TermsPage() {
  const navigate = useNavigate();
  usePageMeta({
    title: 'Terms and Conditions - Wricoo',
    description: 'Read the terms and conditions for using Wricoo\'s content collaboration platform.',
    robots: 'index, follow',
    canonical: 'https://wricoo.com/terms-and-conditions/',
  });

  return (
    <div className="min-h-screen bg-white" data-testid="terms-page">

      {/* Header */}
      <header className="border-b border-blue-200 bg-blue-600 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Link to="/" className="text-blue-200 hover:text-white transition"><ArrowLeft className="w-5 h-5" /></Link>
            <Link to="/" className="text-xl font-bold text-white tracking-tight">Wricoo</Link>
          </div>
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="sm" className="text-white hover:bg-blue-500 hover:text-white" onClick={() => navigate('/login/')}>Sign In</Button>
            <Button size="sm" className="bg-white text-blue-600 hover:bg-blue-50" onClick={() => navigate('/register/')}>Get Started</Button>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="bg-gradient-to-b from-blue-50 to-white py-16 sm:py-20">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 text-center">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-blue-100 mb-6">
            <FileText className="w-8 h-8 text-blue-600" />
          </div>
          <h1 className="font-bold text-gray-900 leading-tight mb-4">Terms and Conditions</h1>
          <p className="text-gray-600 leading-relaxed">Please read these terms carefully before using our Service.</p>
          <p className="text-sm text-gray-400 mt-4">Last updated: February 14, 2026</p>
        </div>
      </section>

      {/* Content */}
      <main className="max-w-3xl mx-auto px-4 sm:px-6 py-12 space-y-16">

        {/* Definitions */}
        <section>
          <div className="flex items-center gap-3 mb-6">
            <div className="w-1 h-8 bg-blue-500 rounded-full" />
            <h2 className="font-bold text-gray-900">Definitions</h2>
          </div>
          <p className="text-gray-600 leading-relaxed mb-4">The following definitions apply throughout these Terms:</p>
          <div className="bg-gray-50 border border-gray-200 rounded-xl p-6 space-y-4">
            {[
              { term: 'Company', def: 'Wricoo, Elite Harmony 2, Vasna-Road, Vadodara (referred to as "We", "Us" or "Our").' },
              { term: 'Service', def: 'The Website and SaaS platform accessible at https://www.wricoo.com/' },
              { term: 'Subscription', def: 'A recurring paid plan (Monthly or Yearly) that provides access to premium features.' },
              { term: 'Free Plan', def: 'The limited-access version of the Service available without payment.' },
              { term: 'You', def: 'The individual or legal entity accessing or using the Service.' },
              { term: 'Country', def: 'Gujarat, India.' },
            ].map((item, i) => (
              <div key={i} className="flex gap-3 text-sm">
                <span className="font-semibold text-gray-900 min-w-[110px] flex-shrink-0">{item.term}</span>
                <span className="text-gray-600">{item.def}</span>
              </div>
            ))}
          </div>
        </section>

        {/* Acknowledgment */}
        <section>
          <div className="flex items-center gap-3 mb-6">
            <div className="w-1 h-8 bg-indigo-500 rounded-full" />
            <h2 className="font-bold text-gray-900">Acknowledgment</h2>
          </div>
          <div className="space-y-4">
            <p className="text-gray-600 leading-relaxed">These Terms govern Your use of the Service and form a legally binding agreement between You and the Company.</p>
            <p className="text-gray-600 leading-relaxed">By accessing or using the Service, You agree to be bound by these Terms. If You disagree with any part, You may not access the Service.</p>
            <div className="bg-blue-50 border border-blue-200 rounded-xl p-5">
              <p className="text-sm text-blue-800">You represent that You are at least 18 years old. The Company does not permit individuals under 18 to use the Service.</p>
            </div>
          </div>
        </section>

        {/* User Accounts */}
        <section>
          <div className="flex items-center gap-3 mb-6">
            <div className="w-1 h-8 bg-violet-500 rounded-full" />
            <h2 className="font-bold text-gray-900">User Accounts</h2>
          </div>
          <p className="text-gray-600 leading-relaxed mb-4">Users may create accounts to access the Service. You are responsible for maintaining the confidentiality of Your login credentials and for all activities under Your account.</p>
          <div className="bg-amber-50 border border-amber-200 rounded-xl p-5">
            <div className="flex items-start gap-3">
              <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-sm font-semibold text-amber-800 mb-1">Account Inactivity</p>
                <p className="text-sm text-amber-700">If You do not log into Your account for a continuous period of twelve (12) months, the Company reserves the right to automatically delete Your account and associated data.</p>
              </div>
            </div>
          </div>
        </section>

        {/* Nature of Service */}
        <section>
          <div className="flex items-center gap-3 mb-6">
            <div className="w-1 h-8 bg-emerald-500 rounded-full" />
            <h2 className="font-bold text-gray-900">Nature of the Service</h2>
          </div>
          <div className="grid sm:grid-cols-2 gap-4">
            {[
              { icon: Briefcase, title: 'Digital SaaS Platform', desc: 'No physical goods are sold or delivered.' },
              { icon: ShieldCheck, title: 'Private Workspace', desc: 'Content is not publicly published by the Company.' },
            ].map((item, i) => (
              <div key={i} className="bg-white border border-gray-200 rounded-xl p-5 hover:border-emerald-200 transition-all">
                <item.icon className="w-6 h-6 text-emerald-500 mb-3" />
                <h3 className="text-base font-semibold text-gray-900 mb-2">{item.title}</h3>
                <p className="text-sm text-gray-600">{item.desc}</p>
              </div>
            ))}
          </div>
          <p className="text-sm text-gray-500 mt-4">Users cannot upload public-facing content, and the Company does not provide API access.</p>
        </section>

        {/* Subscriptions and Billing */}
        <section>
          <div className="flex items-center gap-3 mb-6">
            <div className="w-1 h-8 bg-orange-500 rounded-full" />
            <h2 className="font-bold text-gray-900">Subscriptions and Billing</h2>
          </div>
          <div className="space-y-4">
            <div className="bg-white border border-gray-200 rounded-xl p-6">
              <div className="flex items-center gap-3 mb-3">
                <CreditCard className="w-5 h-5 text-orange-500" />
                <h3 className="text-base font-semibold text-gray-900">Payment Processing</h3>
              </div>
              <p className="text-sm text-gray-600 leading-relaxed">Payments are processed through Razorpay, a third-party payment processor. The Company does not store full payment card information.</p>
            </div>
            <div className="bg-white border border-gray-200 rounded-xl p-6">
              <h3 className="text-base font-semibold text-gray-900 mb-3">Automatic Renewal</h3>
              <p className="text-sm text-gray-600 leading-relaxed">All Subscriptions automatically renew at the end of each billing cycle unless canceled prior to renewal. Email reminders may be sent before renewal.</p>
            </div>
            <div className="bg-red-50 border border-red-200 rounded-xl p-5">
              <div className="flex items-start gap-3">
                <Ban className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm font-semibold text-red-800 mb-1">No Refund Policy</p>
                  <p className="text-sm text-red-700 mb-2">All payments are final and non-refundable.</p>
                  <p className="text-sm text-red-700">If a technical failure occurs at payment time, Razorpay may automatically reverse the transaction within 7-14 business days. If reversal does not occur, contact Us for verification and assistance.</p>
                </div>
              </div>
            </div>
            <div className="bg-slate-50 border border-slate-200 rounded-xl p-5">
              <h3 className="text-sm font-semibold text-gray-900 mb-2">Non-Payment</h3>
              <p className="text-sm text-gray-600">If payment fails or a Subscription expires, the account will automatically revert to the Free Plan and premium features will be locked.</p>
            </div>
          </div>
        </section>

        {/* Acceptable Use */}
        <section>
          <div className="flex items-center gap-3 mb-6">
            <div className="w-1 h-8 bg-red-500 rounded-full" />
            <h2 className="font-bold text-gray-900">Acceptable Use</h2>
          </div>
          <p className="text-gray-600 leading-relaxed mb-4">You agree not to:</p>
          <div className="bg-red-50 border border-red-100 rounded-xl p-6">
            <ul className="space-y-2">
              {[
                'Use the Service for unlawful purposes',
                'Attempt to gain unauthorized access to systems',
                'Interfere with platform security or infrastructure',
                'Reverse engineer, duplicate, copy, or exploit any part of the Service',
                'Use automated systems to access the Service in a harmful way',
              ].map((item, i) => (
                <li key={i} className="flex items-center gap-2.5 text-sm text-red-700">
                  <span className="w-1.5 h-1.5 rounded-full bg-red-400 flex-shrink-0" />{item}
                </li>
              ))}
            </ul>
          </div>
        </section>

        {/* Intellectual Property */}
        <section>
          <div className="flex items-center gap-3 mb-6">
            <div className="w-1 h-8 bg-purple-500 rounded-full" />
            <h2 className="font-bold text-gray-900">Intellectual Property</h2>
          </div>
          <div className="space-y-4">
            <p className="text-gray-600 leading-relaxed">The Service, including its design, branding, software, and functionality, is the exclusive property of the Company.</p>
            <div className="bg-purple-50 border border-purple-200 rounded-xl p-5">
              <p className="text-sm text-purple-800">You retain ownership of content You create within the platform. However, You grant the Company a limited license to store and process such content solely for the purpose of providing the Service.</p>
            </div>
          </div>
        </section>

        {/* Other Sections */}
        <section className="space-y-8">
          <div>
            <h3 className="text-lg font-bold text-gray-900 mb-3">Termination</h3>
            <p className="text-sm text-gray-600 leading-relaxed">We may suspend or terminate access if You breach these Terms. Upon termination, Your right to use the Service will cease immediately.</p>
          </div>
          <div>
            <h3 className="text-lg font-bold text-gray-900 mb-3">Limitation of Liability</h3>
            <p className="text-sm text-gray-600 leading-relaxed mb-2">To the maximum extent permitted by law, the Company's total liability shall not exceed the amount paid by You in the twelve (12) months preceding the claim.</p>
            <p className="text-sm text-gray-600 leading-relaxed">The Company shall not be liable for indirect, incidental, special, consequential, or punitive damages.</p>
          </div>
          <div>
            <h3 className="text-lg font-bold text-gray-900 mb-3">"AS IS" Disclaimer</h3>
            <p className="text-sm text-gray-600 leading-relaxed">The Service is provided "AS IS" and "AS AVAILABLE" without warranties of any kind. We do not guarantee uninterrupted access or error-free performance.</p>
          </div>
        </section>

        {/* Governing Law */}
        <section>
          <div className="flex items-center gap-3 mb-6">
            <div className="w-1 h-8 bg-teal-500 rounded-full" />
            <h2 className="font-bold text-gray-900">Governing Law & Disputes</h2>
          </div>
          <div className="grid sm:grid-cols-2 gap-4">
            <div className="bg-white border border-gray-200 rounded-xl p-5">
              <div className="flex items-center gap-3 mb-3">
                <Scale className="w-5 h-5 text-teal-500" />
                <h3 className="text-base font-semibold text-gray-900">Governing Law</h3>
              </div>
              <p className="text-sm text-gray-600">These Terms shall be governed by the laws of Gujarat, India, without regard to conflict of law provisions.</p>
            </div>
            <div className="bg-white border border-gray-200 rounded-xl p-5">
              <div className="flex items-center gap-3 mb-3">
                <Globe className="w-5 h-5 text-teal-500" />
                <h3 className="text-base font-semibold text-gray-900">Dispute Resolution</h3>
              </div>
              <p className="text-sm text-gray-600">You agree to first attempt to resolve disputes informally by contacting the Company.</p>
            </div>
          </div>
        </section>

        {/* Regional Compliance */}
        <section>
          <div className="flex items-center gap-3 mb-6">
            <div className="w-1 h-8 bg-cyan-500 rounded-full" />
            <h2 className="font-bold text-gray-900">Regional Compliance</h2>
          </div>
          <div className="space-y-3">
            <div className="bg-gray-50 border border-gray-200 rounded-xl p-5">
              <h3 className="text-sm font-semibold text-gray-900 mb-2">EU and UK Users</h3>
              <p className="text-sm text-gray-600">If You are a consumer residing in the EU or UK, You retain mandatory consumer protection rights under applicable law.</p>
            </div>
            <div className="bg-gray-50 border border-gray-200 rounded-xl p-5">
              <h3 className="text-sm font-semibold text-gray-900 mb-2">United States Legal Compliance</h3>
              <p className="text-sm text-gray-600">You represent that You are not located in a country subject to U.S. embargo and are not on any prohibited persons list.</p>
            </div>
          </div>
        </section>

        {/* Other Terms */}
        <section className="space-y-8">
          <div>
            <h3 className="text-lg font-bold text-gray-900 mb-3">Severability</h3>
            <p className="text-sm text-gray-600 leading-relaxed">If any provision of these Terms is deemed invalid, the remaining provisions shall remain in full force.</p>
          </div>
          <div>
            <h3 className="text-lg font-bold text-gray-900 mb-3">Changes to These Terms</h3>
            <p className="text-sm text-gray-600 leading-relaxed">We reserve the right to modify these Terms at any time. Continued use of the Service after changes constitutes acceptance of the revised Terms.</p>
          </div>
        </section>

        {/* Contact */}
        <section className="bg-gradient-to-br from-blue-50 to-indigo-50 border border-blue-100 rounded-2xl p-8 sm:p-10">
          <div className="flex items-center gap-3 mb-4">
            <Mail className="w-6 h-6 text-blue-600" />
            <h2 className="font-bold text-gray-900">Contact Us</h2>
          </div>
          <p className="text-gray-600 leading-relaxed mb-4">If you have any questions about these Terms and Conditions, please contact us:</p>
          <a href="mailto:contact@wricoo.com" className="inline-flex items-center gap-2 text-blue-600 hover:text-blue-700 font-medium transition">
            contact@wricoo.com
          </a>
        </section>
      </main>

      {/* Footer */}
      <footer className="py-10 bg-gray-50 border-t border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex flex-wrap items-center justify-between gap-4">
          <p className="text-sm text-gray-500">&copy; {new Date().getFullYear()} Wricoo. All rights reserved.</p>
          <nav className="flex gap-4 text-sm text-gray-500">
            <a href="/about/" className="hover:text-gray-900 transition">About</a>
            <a href="/pricing/" className="hover:text-gray-900 transition">Pricing</a>
            <a href="/tools/" className="hover:text-gray-900 transition">Tools</a>
            <a href="/privacy-policy/" className="hover:text-gray-900 transition">Privacy</a>
            <a href="/terms-and-conditions/" className="hover:text-gray-900 transition">Terms</a>
          </nav>
        </div>
      </footer>
    </div>
  );
}
